import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import ScrollToggle from 'components/ScrollToggle/ScrollToggle'
import SAlert from 'components/SAlert'
import toast from 'react-hot-toast'
import moment from 'moment'
import {
  PDFCheckBox,
  PDFDocument,
  PDFDropdown,
  PDFOptionList,
  PDFRadioGroup,
  PDFTextField,
} from 'pdf-lib'
import { useTranslation } from 'react-i18next'
import { Document, Page, pdfjs } from 'react-pdf'
import { useSelector } from 'react-redux'
import Select, { components } from 'react-select'

import { useFourThreeThreeBApi } from 'resources/hooks/api/fourThreeThreeBApiHook'
import { getBranch, getUser } from 'store/user/selectors'
import { preFillForm433b } from './FormHelper433b'
import { FormsListEng, FormsListSn } from '../formConfig/FormsList'
import { useNavigationBlocker } from '../taxHooks/useNavigationBlocker'
import { NavigationBlockerDialog } from '../components/NavigationBlockerDialog'
import publishBudgetIcon from '../../../assets/images/billing-icon-w.svg'
import PdfLoadingView from '../components/PdfLoadingView'
import PdfErrorView from '../components/PdfErrorView'

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

const PAGE_WIDTH = 800
const FORM_KEY = 'f433b'

interface DraftOption {
  label: string
  value: string | number
  isPublished: boolean
  lang: string
}

const normalizePdfUrl = (url: string): string => {
  if (!url) return url
  if (url.includes('/files/?p=') && !url.includes('raw=1')) {
    return url.includes('?') ? `${url}&raw=1` : `${url}?raw=1`
  }
  return url
}

const getFieldTypeString = (field: any): string => {
  if (field instanceof PDFTextField) return 'PDFTextField'
  if (field instanceof PDFCheckBox) return 'PDFCheckBox'
  if (field instanceof PDFRadioGroup) return 'PDFRadioGroup'
  if (field instanceof PDFDropdown) return 'PDFDropdown'
  if (field instanceof PDFOptionList) return 'PDFOptionList'
  return 'Unknown'
}

const getFromDateString = (monthStr: string | null) => {
  if (!monthStr) return null
  return `${monthStr}-01`
}

const getToDateString = (monthStr: string | null) => {
  if (!monthStr) return null
  const [year, month] = monthStr.split('-').map(Number)
  const lastDay = new Date(year, month, 0).getDate()
  return `${monthStr}-${String(lastDay).padStart(2, '0')}`
}

const getCurrentUiLang = (): 'en-US' | 'es' => {
  const getlang = localStorage.getItem('i18nextLng')
  return getlang === 'es' || getlang?.startsWith('es-') ? 'es' : 'en-US'
}

const LANGUAGE_OPTIONS = [
  { value: 'en-US', label: 'English (Form 433-B)' },
  { value: 'es', label: 'Español (Formulario 433-B)' },
]

export default function FormFourThreeThreeB() {
  const { t } = useTranslation('scenes.TaxResolutions.index')
  const branchData = useSelector(getBranch)
  const userData = useSelector(getUser)
  const pdfDocRef = useRef<PDFDocument | null>(null)
  const formRef = useRef<any>(null)
  const masterBytesRef = useRef<Uint8Array | null>(null)
  const detailsRef = useRef<any | null>(null)
  const loadTokenRef = useRef(0)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const [documentFile, setDocumentFile] = useState<{ data: Uint8Array } | null>(
    null
  )
  const [numPages, setNumPages] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)
  const [fieldDefs, setFieldDefs] = useState<any[]>([])
  const [values, setValues] = useState<Record<string, any>>({})
  const [pageMeta, setPageMeta] = useState<Record<number, any>>({})
  const [revision, setRevision] = useState<string | null>(null)
  const [isLatestLoaded, setIsLatestLoaded] = useState<boolean>(false)
  const [newerVersionAvailable, setNewerVersionAvailable] =
    useState<boolean>(false)
  const [isPublished, setIsPublished] = useState<boolean>(false)
  const [publishAlert, setPublishAlert] = useState<boolean>(false)
  const [zoom] = useState<number>(1)
  const [isDirty, setIsDirty] = useState(false)

  // Draft state management
  const [draftList, setDraftList] = useState<DraftOption[]>([])
  const [selectedDraft, setSelectedDraft] = useState<DraftOption | null>(null)

  // Explicitly track the active language of the loaded form on screen
  const [activeFormLang, setActiveFormLang] = useState<'en-US' | 'es'>(
    getCurrentUiLang()
  )

  const [draftNameModalOpen, setDraftNameModalOpen] = useState<boolean>(false)
  const [pendingDraftAction, setPendingDraftAction] = useState<
    'save' | 'publish' | null
  >(null)
  const [draftNameInput, setDraftNameInput] = useState<string>('')
  const [saveMode, setSaveMode] = useState<'update' | 'new'>('update')

  // New Form Language Modal state
  const [newFormLangModalOpen, setNewFormLangModalOpen] =
    useState<boolean>(false)
  const [selectedNewLang, setSelectedNewLang] = useState<'en-US' | 'es'>(
    getCurrentUiLang()
  )

  const { showPrompt, confirmNavigation, cancelNavigation } =
    useNavigationBlocker(isDirty)

  // Section 5 Month Range State
  const [fromMonth, setFromMonth] = useState<string>('')
  const [toMonth, setToMonth] = useState<string>('')

  const {
    getTaxResolutionDetails,
    saveTaxResolutionDetails,
    publishTaxResolutionDetails,
    getTaxResolutionPnl,
    getTaxResolutionDraft,
  } = useFourThreeThreeBApi()

  const cloneBytes = (bytes: Uint8Array): Uint8Array => new Uint8Array(bytes)

  const getFieldLabel = (field: any): string => {
    try {
      const alt = field.acroField.getAlternateName()
      if (alt && alt.asString().trim() !== '') return alt.asString().trim()
    } catch (e) {
      /* no-op */
    }
    const rawName = field.getName()
    const parts = rawName.split(/[./]/)
    const clean = parts[parts.length - 1]
      .replace(/\[\d+\]/g, '')
      .replace(/[-_]/g, ' ')
      .trim()
    return clean || rawName
  }

  const getFieldOptions = (field: any, type: string) => {
    try {
      if (
        type === 'PDFDropdown' ||
        type === 'PDFOptionList' ||
        type === 'PDFRadioGroup'
      ) {
        return field.getOptions()
      }
    } catch {
      /* no-op */
    }
    return []
  }

  const getInitialValue = (field: any, type: string) => {
    try {
      switch (type) {
        case 'PDFTextField':
          return field.getText() || ''
        case 'PDFCheckBox':
          return field.isChecked()
        case 'PDFRadioGroup':
          return field.getSelected() || ''
        case 'PDFDropdown':
          return field.getSelected()?.[0] || ''
        case 'PDFOptionList':
          return field.getSelected() || []
        default:
          return ''
      }
    } catch {
      return ''
    }
  }

  const applyValuesToForm = useCallback(
    (formInstance: any, valuesToApply: Record<string, any>) => {
      if (!formInstance) return
      Object.entries(valuesToApply).forEach(([fieldName, val]) => {
        try {
          const field = formInstance.getField(fieldName)
          const type = getFieldTypeString(field)

          switch (type) {
            case 'PDFTextField': {
              const maxLen = (field as PDFTextField).getMaxLength()
              let text = val !== null && val !== undefined ? String(val) : ''
              if (maxLen !== undefined && text.length > maxLen) {
                text = text.slice(0, maxLen)
              }
              ;(field as PDFTextField).setText(text)
              break
            }
            case 'PDFCheckBox':
              val ? field.check() : field.uncheck()
              break
            case 'PDFRadioGroup':
            case 'PDFDropdown':
              if (val) field.select(String(val))
              break
            case 'PDFOptionList':
              if (val) {
                const listVal = Array.isArray(val)
                  ? val.map(String)
                  : [String(val)]
                field.select(listVal)
              }
              break
          }
        } catch {
          /* Ignore non-matching PDF fields */
        }
      })
    },
    []
  )

  const fetchTaxResolutionDetails = useCallback(
    (
      draftId?: string | number | null,
      langOverride?: string
    ): Promise<any | null> => {
      const lang = langOverride || activeFormLang || getCurrentUiLang()
      const params: Record<string, any> = {
        form_type: FORM_KEY,
        branch_id: branchData?.id,
        lang,
      }

      params.draft_id = draftId !== undefined && draftId !== null ? draftId : 0

      return new Promise((resolve) => {
        getTaxResolutionDetails(
          params,
          (_message: string, resp: any) => {
            const details = resp?.data || resp
            resolve(details && typeof details === 'object' ? details : null)
          },
          () => resolve(null)
        )
      })
    },
    [branchData?.id, activeFormLang, getTaxResolutionDetails]
  )

  const loadPdfFromUrl = useCallback(
    async (
      formUrl: string,
      revisionLabel: string | null,
      savedFieldValues: Record<string, any> | null | undefined,
      token: number,
      formLang: 'en-US' | 'es',
      savedFromMonth?: string,
      savedToMonth?: string
    ) => {
      const directUrl = normalizePdfUrl(formUrl)
      const response = await fetch(directUrl)

      if (!response.ok) {
        throw new Error(`Failed to fetch PDF. Status: ${response.status}`)
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('text/html')) {
        throw new Error(
          `Expected a PDF file, but received an HTML webpage. Check if the URL is a preview page instead of a raw download link.`
        )
      }

      const buffer = await response.arrayBuffer()
      if (token !== loadTokenRef.current) return

      const masterBytes = new Uint8Array(buffer)
      const isPdfHeader =
        masterBytes[0] === 0x25 &&
        masterBytes[1] === 0x50 &&
        masterBytes[2] === 0x44 &&
        masterBytes[3] === 0x46

      if (!isPdfHeader) {
        throw new Error('Downloaded file does not contain a valid %PDF header.')
      }

      masterBytesRef.current = masterBytes

      const pdfjsDoc = await pdfjs.getDocument({
        data: cloneBytes(masterBytes),
      }).promise

      if (token !== loadTokenRef.current) {
        await pdfjsDoc.destroy()
        return
      }

      const meta: Record<number, any> = {}
      for (let i = 1; i <= pdfjsDoc.numPages; i++) {
        const page = await pdfjsDoc.getPage(i)
        const unscaledViewport = page.getViewport({ scale: 1 })
        const baseScale = PAGE_WIDTH / unscaledViewport.width
        meta[i - 1] = { unscaledViewport, baseScale }
      }

      const pageCount = pdfjsDoc.numPages
      await pdfjsDoc.destroy()
      if (token !== loadTokenRef.current) return

      setRevision(revisionLabel)
      setPageMeta(meta)
      setNumPages(pageCount)

      const pdfDoc = await PDFDocument.load(cloneBytes(masterBytes))
      const form = pdfDoc.getForm()
      const libPages = pdfDoc.getPages()
      const fields = form.getFields()

      const findPageIndex = (widgetDict: any) => {
        for (let i = 0; i < libPages.length; i++) {
          const annots = libPages[i].node.Annots()
          if (!annots) continue
          for (let j = 0; j < annots.size(); j++) {
            if (pdfDoc.context.lookup(annots.get(j)) === widgetDict) return i
          }
        }
        return -1
      }

      const defs: any[] = []
      const initialValues: Record<string, any> = {}

      fields.forEach((field) => {
        const name = field.getName()
        const type = getFieldTypeString(field)
        const maxLength =
          field instanceof PDFTextField ? field.getMaxLength() : undefined

        field.acroField.getWidgets().forEach((widget) => {
          const pageIndex = findPageIndex(widget.dict)
          if (pageIndex === -1) return

          defs.push({
            key: `${name}-${pageIndex}-${widget.getRectangle().x}-${
              widget.getRectangle().y
            }`,
            name,
            label: getFieldLabel(field),
            type,
            pageIndex,
            rect: widget.getRectangle(),
            maxLength,
            options: getFieldOptions(field, type),
          })
        })

        initialValues[name] = getInitialValue(field, type)
      })

      let savedValuesNormalized: Record<string, any> = {}
      if (savedFieldValues) {
        const fieldNameByLower = new Map<string, string>()
        fields.forEach((f) =>
          fieldNameByLower.set(f.getName().toLowerCase(), f.getName())
        )

        Object.entries(savedFieldValues).forEach(([savedKey, val]) => {
          if (savedKey in initialValues) {
            savedValuesNormalized[savedKey] = val
          } else {
            const realName = fieldNameByLower.get(savedKey.toLowerCase())
            if (realName) savedValuesNormalized[realName] = val
          }
        })
      }

      const currentValues = { ...initialValues, ...savedValuesNormalized }
      // Pass the specific formLang to preFillForm433b
      const finalValues = preFillForm433b(
        currentValues,
        branchData,
        userData,
        null,
        false,
        formLang
      )

      if (savedFromMonth) {
        const fromDate = getFromDateString(savedFromMonth)
        finalValues['topmostSubform[0].Page6[0].p6_1_Sec5[0]'] =
          moment(fromDate).format('MMDDYYYY')
      }
      if (savedToMonth) {
        const toDate = getToDateString(savedToMonth)
        finalValues['topmostSubform[0].Page6[0].p6_2_Sec5[0]'] =
          moment(toDate).format('MMDDYYYY')
      }

      applyValuesToForm(form, finalValues)
      pdfDocRef.current = pdfDoc
      formRef.current = form
      setFieldDefs(defs)
      setValues(finalValues)
      setDocumentFile({ data: cloneBytes(masterBytes) })
      setIsDirty(false)
    },
    [branchData, userData, applyValuesToForm]
  )

  const loadForm = useCallback(
    async (targetDraft?: DraftOption | null, langOverride?: 'en-US' | 'es') => {
      const targetDraftId =
        targetDraft !== undefined
          ? targetDraft?.value
          : selectedDraft?.value ?? null

      const effectiveLang: 'en-US' | 'es' =
        langOverride ||
        (targetDraft?.lang as 'en-US' | 'es') ||
        (selectedDraft?.lang as 'en-US' | 'es') ||
        getCurrentUiLang()

      // Keep activeFormLang synchronized with the loaded form
      setActiveFormLang(effectiveLang)

      const isSpanish =
        effectiveLang === 'es' || effectiveLang?.startsWith('es-')
      const staticFallbackUrl = isSpanish
        ? FormsListSn[FORM_KEY]
        : FormsListEng[FORM_KEY]

      const token = ++loadTokenRef.current

      setLoading(true)
      setIsLoading(true)
      setHasError(false)

      pdfDocRef.current = null
      formRef.current = null
      masterBytesRef.current = null
      detailsRef.current = null
      setDocumentFile(null)
      setNumPages(0)
      setFieldDefs([])
      setValues({})
      setRevision(null)
      setIsLatestLoaded(false)
      setNewerVersionAvailable(false)
      setPageMeta({})

      try {
        const details = await fetchTaxResolutionDetails(
          targetDraftId,
          effectiveLang
        )

        if (token !== loadTokenRef.current) return
        detailsRef.current = details

        const formUrl = details?.data?.pdf_url || staticFallbackUrl
        if (!formUrl) {
          setHasError(true)
          return
        }

        const revisionLabel = details?.data?.form_revision ?? null

        const parseMonthValue = (
          rawDate: any,
          fallbackMonth: string = ''
        ): string => {
          if (!rawDate) return fallbackMonth
          const str = String(rawDate).trim()
          const parsedMmds = moment(str, 'MMDDYYYY', true)
          if (parsedMmds.isValid()) return parsedMmds.format('YYYY-MM')
          const parsedGeneral = moment(str)
          if (parsedGeneral.isValid()) return parsedGeneral.format('YYYY-MM')
          return fallbackMonth
        }

        const isNewForm = targetDraftId === null || targetDraftId === undefined
        const rawFieldValues = isNewForm
          ? null
          : details?.data?.field_value ?? null

        const rawP6_1 =
          rawFieldValues?.['topmostSubform[0].Page6[0].p6_1_Sec5[0]']
        const rawP6_2 =
          rawFieldValues?.['topmostSubform[0].Page6[0].p6_2_Sec5[0]']

        const parsedFromMonth = parseMonthValue(
          rawP6_1,
          isNewForm ? '' : details?.data?.from_month || ''
        )
        const parsedToMonth = parseMonthValue(
          rawP6_2,
          isNewForm ? '' : details?.data?.to_month || ''
        )

        setFromMonth(parsedFromMonth)
        setToMonth(parsedToMonth)

        await loadPdfFromUrl(
          formUrl,
          revisionLabel,
          rawFieldValues,
          token,
          effectiveLang,
          parsedFromMonth,
          parsedToMonth
        )
        if (token !== loadTokenRef.current) return

        setIsLatestLoaded(false)
        setIsPublished(isNewForm ? false : !!details?.data?.is_published)
        setNewerVersionAvailable(!!details?.data?.has_newer_version)
      } catch (err) {
        if (token === loadTokenRef.current) {
          setHasError(true)
          toast.error(t('failed_to_load_pdf') || 'Failed to load Form 433-B.')
        }
      } finally {
        if (token === loadTokenRef.current) {
          setLoading(false)
          setIsLoading(false)
        }
      }
    },
    [selectedDraft, fetchTaxResolutionDetails, loadPdfFromUrl, t]
  )

  const getDraftList = useCallback(
    (onSuccess?: (options: DraftOption[]) => void) => {
      const params = {
        branch_id: branchData?.id,
        form_type: FORM_KEY,
      }

      getTaxResolutionDraft(
        params,
        (_message: string, resp: any) => {
          const rawList = resp?.data?.data || resp?.data || []
          const list: DraftOption[] = (
            Array.isArray(rawList) ? rawList : []
          ).map((item: any) => ({
            label: item.draft_name || `Draft #${item.id}`,
            value: item.id,
            isPublished: !!item.is_published,
            lang: item.lang || (item.language === 'es' ? 'es' : 'en-US'),
          }))
          setDraftList(list)
          if (onSuccess) onSuccess(list)
        },
        (errorMsg: string) => {
          toast.error(errorMsg || 'Failed to fetch draft list.')
          if (onSuccess) onSuccess([])
        }
      )
    },
    [branchData?.id, getTaxResolutionDraft]
  )

  useEffect(() => {
    getDraftList((list) => {
      if (list && list.length > 0) {
        const firstDraft = list[0]
        setSelectedDraft(firstDraft)
        setActiveFormLang(
          (firstDraft.lang as 'en-US' | 'es') || getCurrentUiLang()
        )
        loadForm(firstDraft)
      } else {
        setSelectedDraft(null)
        loadForm(null)
      }
    })
  }, [branchData?.id])

  const handleDraftChange = (option: DraftOption | null) => {
    if (
      isDirty &&
      !window.confirm(
        t('unsaved_changes_confirm', 'You have unsaved changes. Discard them?')
      )
    ) {
      return
    }
    setSelectedDraft(option)
    if (option) {
      setActiveFormLang(option.lang as 'en-US' | 'es')
    }
    loadForm(option)
  }

  const handleOpenNewFormModal = () => {
    if (
      isDirty &&
      !window.confirm(
        t('unsaved_changes_confirm', 'You have unsaved changes. Discard them?')
      )
    ) {
      return
    }
    setSelectedNewLang(getCurrentUiLang())
    setNewFormLangModalOpen(true)
  }

  const handleConfirmNewForm = () => {
    setNewFormLangModalOpen(false)
    setSelectedDraft(null)
    setFromMonth('')
    setToMonth('')
    // Update activeFormLang to the chosen language immediately
    setActiveFormLang(selectedNewLang)
    loadForm(
      {
        label: '',
        value: null as any,
        isPublished: false,
        lang: selectedNewLang,
      },
      selectedNewLang
    )
  }

  const loadLatestVersion = useCallback(async () => {
    const details = detailsRef.current
    if (!details?.data?.latest_pdf_url) return

    const token = ++loadTokenRef.current
    setIsLoading(true)
    setHasError(false)

    try {
      await loadPdfFromUrl(
        details?.data.latest_pdf_url,
        details?.data.latest_revision ?? null,
        details?.data.field_value,
        token,
        activeFormLang,
        fromMonth,
        toMonth
      )
      if (token !== loadTokenRef.current) return

      setIsLatestLoaded(true)
      setNewerVersionAvailable(false)
    } catch (err) {
      if (token === loadTokenRef.current) {
        setHasError(true)
        toast.error(t('failed_to_load_pdf') || 'Failed to load latest version.')
      }
    } finally {
      if (token === loadTokenRef.current) {
        setIsLoading(false)
      }
    }
  }, [loadPdfFromUrl, activeFormLang, fromMonth, toMonth, t])

  const updateField = (name: string, type: string, value: any) => {
    const form = formRef.current
    let sanitizedValue = value

    if (type === 'PDFTextField' && form) {
      try {
        const field = form.getField(name) as PDFTextField
        const maxLen = field.getMaxLength()
        if (
          maxLen !== undefined &&
          typeof value === 'string' &&
          value.length > maxLen
        ) {
          sanitizedValue = value.slice(0, maxLen)
        }
      } catch {
        /* ignore */
      }
    }

    setValues((prev) => ({ ...prev, [name]: sanitizedValue }))
    if (!form) return
    setIsDirty(true)

    try {
      const field = form.getField(name)
      const fieldType = getFieldTypeString(field)

      switch (fieldType) {
        case 'PDFTextField':
          ;(field as PDFTextField).setText(
            sanitizedValue !== null && sanitizedValue !== undefined
              ? String(sanitizedValue)
              : ''
          )
          break
        case 'PDFCheckBox': {
          const cb = field as PDFCheckBox
          sanitizedValue ? cb.check() : cb.uncheck()
          break
        }
        case 'PDFRadioGroup':
          ;(field as PDFRadioGroup).select(sanitizedValue)
          break
        case 'PDFDropdown':
          ;(field as PDFDropdown).select(sanitizedValue)
          break
        case 'PDFOptionList':
          ;(field as PDFOptionList).select(sanitizedValue)
          break
      }
    } catch (err) {
      /* ignore */
    }
  }

  const handleApplyDates = () => {
    if ((fromMonth && !toMonth) || (!fromMonth && toMonth)) {
      toast.error(t('date_error',"Please select both From and To months."))
      return
    }

    setIsLoading(true)

    const params = {
      from_date: getFromDateString(fromMonth),
      to_date: getToDateString(toMonth),
      from_month: fromMonth || '',
      to_month: toMonth || '',
      form_type: FORM_KEY,
      branch_id: branchData?.id,
      draft_id: selectedDraft?.value ?? 0,
      lang: activeFormLang, // Use the active form's language
    }

    getTaxResolutionPnl(
      params,
      (_message: string, resp: any) => {
        const pnlData = resp?.data?.data || resp?.data || resp

        const updatedFields = preFillForm433b(
          values,
          branchData,
          userData,
          pnlData,
          true
        )

        if (formRef.current) {
          applyValuesToForm(formRef.current, updatedFields)
        }

        setValues(updatedFields)
        setIsDirty(true)
        setIsLoading(false)
        toast.success(t('filterSuccess', 'Income data updated successfully.'))
      },
      (errorMsg: string) => {
        setIsLoading(false)
        toast.error(errorMsg || 'Failed to fetch income data.')
      }
    )
  }

  const handleResetDates = () => {
    setFromMonth('')
    setToMonth('')

    updateField('topmostSubform[0].Page6[0].p6_1_Sec5[0]', 'PDFTextField', '')
    updateField('topmostSubform[0].Page6[0].p6_2_Sec5[0]', 'PDFTextField', '')

    const resetFields = preFillForm433b(
      values,
      branchData,
      userData,
      null,
      false,
      activeFormLang
    )

    if (formRef.current) {
      applyValuesToForm(formRef.current, resetFields)
    }

    setValues(resetFields)
    setIsDirty(true)
  }

  const handleInitiateSave = () => {
    setPendingDraftAction('save')
    setSaveMode(selectedDraft ? 'update' : 'new')
    setDraftNameInput(selectedDraft?.label || '')
    setDraftNameModalOpen(true)
  }

  const handleInitiatePublish = () => {
    setPendingDraftAction('publish')
    setSaveMode(selectedDraft ? 'update' : 'new')
    setDraftNameInput(selectedDraft?.label || '')
    setDraftNameModalOpen(true)
  }

const getMonthYear = (value:any) => {
  const [year, month] = value.split('.');

  return {
    month: Number(month),
    year: Number(year),
  };
};

  const executeSaveDraft = (draftName: string, isNew: boolean) => {
    setIsLoading(true)

    // Save using activeFormLang (persisting the language of the form currently on screen)
    const effectiveDraftId = isNew ? null : selectedDraft?.value ?? null

    const params: Record<string, any> = {
      draft_name: draftName,
      draft_id: effectiveDraftId,
      form_revision: revision,
      field_value: values,
      from_date: getFromDateString(fromMonth) || '',
      to_date: getToDateString(toMonth) || '',
      from_month: fromMonth || '',
      to_month: toMonth || '',
      form_type: FORM_KEY,
      branch_id: branchData?.id,
      generated_at: new Date().toISOString(),
      lang: activeFormLang,
      month:getMonthYear(revision).month,
      year:getMonthYear(revision).year
    }

    saveTaxResolutionDetails(
      params,
      (_message: string, resp: any) => {
        toast.success(
          resp?.message ||
            t('draft_saved_successfully', 'Draft saved successfully.')
        )
        setIsLoading(false)
        setIsDirty(false)

        const savedId =
          resp?.data?.data?.id || resp?.data?.id || effectiveDraftId
        getDraftList((list) => {
          if (savedId) {
            const matched = list.find(
              (d) => String(d.value) === String(savedId)
            )
            if (matched) setSelectedDraft(matched)
          }
        })
      },
      (error: any) => {
        toast.error(
          error || t('something_went_wrong_publishing', 'Failed to save draft.')
        )
        setIsLoading(false)
      }
    )
  }

  const executePublishDraft = (draftName: string, isNew: boolean) => {
    setIsLoading(true)

    const effectiveDraftId = isNew ? null : selectedDraft?.value ?? null

    const saveParams: Record<string, any> = {
      draft_name: draftName,
      draft_id: effectiveDraftId,
      form_revision: revision,
      field_value: values,
      from_date: getFromDateString(fromMonth),
      to_date: getToDateString(toMonth),
      from_month: fromMonth || null,
      to_month: toMonth || null,
      form_type: FORM_KEY,
      branch_id: branchData?.id,
      generated_at: new Date().toISOString(),
      lang: activeFormLang,
      month:getMonthYear(revision).month,
      year:getMonthYear(revision).year
    }

    saveTaxResolutionDetails(
      saveParams,
      (saveResp: any) => {
        const currentDraftId =
          saveResp?.data?.data?.id || saveResp?.data?.id || effectiveDraftId

        const publishPayload = {
          ...saveParams,
          draft_id: currentDraftId,
          lang: activeFormLang,
        }

        publishTaxResolutionDetails(
          publishPayload,
          (_message: string, resp: any) => {
            setIsPublished(true)
            toast.success(
              resp?.message ||
                t('form_published_successfully', 'Published successfully.')
            )
            setIsLoading(false)
            setPublishAlert(false)
            setIsDirty(false)

            const publishedId = resp?.data?.data?.id || currentDraftId
            getDraftList((list) => {
              if (publishedId) {
                const matched = list.find(
                  (d) => String(d.value) === String(publishedId)
                )
                if (matched) setSelectedDraft(matched)
              }
            })
          },
          (error: any) => {
            toast.error(
              error ||
                t('something_went_wrong_publishing', 'Failed to publish form.')
            )
            setIsLoading(false)
            setPublishAlert(false)
          }
        )
      },
      (error: any) => {
        toast.error(
          error ||
            t(
              'something_went_wrong_publishing',
              'Failed to save before publishing.'
            )
        )
        setIsLoading(false)
        setPublishAlert(false)
      }
    )
  }

  const handleModalSubmit = () => {
    const trimmed = draftNameInput.trim()
    if (!trimmed) {
      toast.error(t('draft_name_error','Please enter a draft name.'))
      return
    }
    setDraftNameModalOpen(false)

    const isNewDraft = !selectedDraft || saveMode === 'new'

    if (pendingDraftAction === 'save') {
      executeSaveDraft(trimmed, isNewDraft)
    } else if (pendingDraftAction === 'publish') {
      setPublishAlert(true)
    }
    setPendingDraftAction(null)
  }

  const downloadPdf = async () => {
    if (!pdfDocRef.current) return
    const bytes: any = await pdfDocRef.current.save()
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${selectedDraft?.label || 'Form_433-B'}.pdf`
    a.click()
    URL.revokeObjectURL(url)
    toast.success(t('form_downloaded_successfully'))
  }

  const getViewport = (pageIndex: number) => {
    const meta = pageMeta[pageIndex]
    if (!meta) return null
    return meta.unscaledViewport.clone({ scale: meta.baseScale * zoom })
  }

  const rectToStyle = (def: any) => {
    const viewport = getViewport(def.pageIndex)
    if (!viewport) return { display: 'none' }

    const { x, y, width, height } = def.rect
    const [sx1, sy1] = viewport.convertToViewportPoint(x, y)
    const [sx2, sy2] = viewport.convertToViewportPoint(x + width, y + height)

    return {
      position: 'absolute' as const,
      left: Math.min(sx1, sx2),
      top: Math.min(sy1, sy2),
      width: Math.abs(sx2 - sx1),
      height: Math.abs(sy2 - sy1),
    }
  }

  const renderOverlay = (def: any) => {
    const { name, type, maxLength } = def
    const baseStyle: React.CSSProperties = {
      ...rectToStyle(def),
      border: '1px solid transparent',
      background: 'transparent',
      fontSize: 11,
      zIndex: 10,
      pointerEvents: 'auto',
      borderRadius: '2px',
      color: '#000',
    }

    switch (type) {
      case 'PDFTextField':
        return (
          <input
            key={def.key}
            value={values[name] ?? ''}
            onChange={(e) => updateField(name, type, e.target.value)}
            maxLength={maxLength}
            style={baseStyle}
            disabled={isPublished}
          />
        )
      case 'PDFCheckBox':
        return (
          <input
            key={def.key}
            type="checkbox"
            checked={!!values[name]}
            onChange={(e) => updateField(name, type, e.target.checked)}
            style={{
              ...baseStyle,
              background: 'transparent',
              cursor: 'pointer',
              color: '#000',
            }}
            disabled={isPublished}
          />
        )
      case 'PDFRadioGroup':
      case 'PDFDropdown':
        return (
          <select
            key={def.key}
            value={values[name] ?? ''}
            onChange={(e) => updateField(name, type, e.target.value)}
            style={baseStyle}
            disabled={isPublished}
          >
            <option value="">--</option>
            {def.options.map((opt: string) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        )
      case 'PDFOptionList':
        return (
          <select
            key={def.key}
            multiple
            value={values[name] ?? []}
            onChange={(e) =>
              updateField(
                name,
                type,
                Array.from(e.target.selectedOptions).map((o) => o.value)
              )
            }
            style={baseStyle}
            disabled={isPublished}
          >
            {def.options.map((opt: string) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        )
      default:
        return null
    }
  }

  const ready =
    !loading &&
    !hasError &&
    numPages > 0 &&
    Object.keys(pageMeta).length === numPages &&
    !!documentFile
  
  const isInvalidDateRange =
  !fromMonth || !toMonth || toMonth <= fromMonth
  const CustomOption = (props: any) => {
    const { data, isSelected, isFocused } = props
    const isEs = data.lang === 'es' || data.lang?.startsWith('es-')

    return (
      <components.Option {...props}>
        <div
          className="d-flex align-items-center justify-content-between w-100"
          style={{
            padding: '6px 4px',
            borderRadius: '6px',
            backgroundColor: isSelected
              ? '#eff6ff'
              : isFocused
              ? '#f3f4f6'
              : 'transparent',
          }}
        >
          <span
            style={{
              fontSize: '13px',
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? '#1d4ed8' : '#111827',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {data.label}
          </span>

          <div className="d-flex align-items-center gap-1 flex-shrink-0 ms-2">
            <span
              style={{
                fontSize: '10px',
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: '999px',
                lineHeight: '14px',
                color: data.isPublished ? '#166534' : '#92400e',
                backgroundColor: data.isPublished ? '#dcfce7' : '#fef3c7',
              }}
            >
              {data.isPublished ? t('published') : t('saved')}
            </span>

            <span
              style={{
                fontSize: '10px',
                fontWeight: 700,
                padding: '2px 6px',
                minWidth: '26px',
                textAlign: 'center',
                borderRadius: '999px',
                lineHeight: '14px',
                color: isEs ? '#075985' : '#374151',
                backgroundColor: isEs ? '#e0f2fe' : '#e5e7eb',
              }}
            >
              {isEs ? 'ES' : 'EN'}
            </span>
          </div>
        </div>
      </components.Option>
    )
  }

  const CustomSingleValue = (props: any) => {
    const { data } = props
    return (
      <components.SingleValue {...props}>
        <div className="d-flex align-items-center justify-content-between w-100 pe-1">
          <span
            style={{
              fontSize: '13px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {data.label}
          </span>
        </div>
      </components.SingleValue>
    )
  }

  return (
    <div className="main-container flex-grow-1">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center flex-wrap pb-1 pt-2">
          <div>
            <h1 className="h5 font-weight-700 mb-0 d-inline-flex text-black">
              {t('f433b', 'Form 433-B')}
            </h1>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            {/* Section 5: Monthly Income & Expense Period Filter */}
            {!isPublished && (
              <Box className="filter-boxes px-2 py-1 rounded-3 bg-white shadow-sm border">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="small text-black fw-bold">
                    <i className="fa-solid fa-calendar-days text-primary me-1"></i>
                    {t('filterHeader', 'Section 5 Income/Expense Range')}
                  </span>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="filter-box">
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-light text-muted small px-2">
                        {t('from', 'From')}
                      </span>
                      <input
                        type="month"
                        className="form-control form-control-sm bg-white"
                        value={fromMonth}
                        onChange={(e) => {
                          setFromMonth(e.target.value)
                          const fromDate = getFromDateString(e.target.value)
                          updateField(
                            'topmostSubform[0].Page6[0].p6_1_Sec5[0]',
                            'PDFTextField',
                            moment(fromDate).format('MMDDYYYY')
                          )
                        }}
                        style={{
                          minWidth: '130px',
                          color: '#000000',
                          fontSize: '12px',
                        }}
                        max={new Date().toISOString().slice(0, 7)}
                      />
                    </div>
                  </div>

                  <div className="filter-box">
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-light text-muted small px-2">
                        {t('to', 'To')}
                      </span>
                      <input
                        type="month"
                        className="form-control form-control-sm bg-white"
                        value={toMonth}
                        onChange={(e) => {
                          setToMonth(e.target.value)
                          const toDate = getToDateString(e.target.value)
                          updateField(
                            'topmostSubform[0].Page6[0].p6_2_Sec5[0]',
                            'PDFTextField',
                            moment(toDate).format('MMDDYYYY')
                          )
                        }}
                        min={fromMonth || undefined}
                        style={{
                          minWidth: '130px',
                          color: '#000000',
                          fontSize: '12px',
                          
                        }}
                        max={new Date().toISOString().slice(0, 7)}
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleApplyDates}
                    className="btn btn-sm btn-primary px-2 py-1 d-flex align-items-center gap-1"
                    disabled={isLoading || isInvalidDateRange}
                    style={{ fontSize: '12px' }}
                  >
                    <i className="fa-solid fa-check"></i>
                    {t('applyBtn', 'Apply')}
                  </button>

                  <button
                    type="button"
                    onClick={handleResetDates}
                    className="btn btn-sm btn-outline-secondary px-2 py-1 d-flex align-items-center gap-1"
                    disabled={isLoading}
                    style={{ fontSize: '12px' }}
                  >
                    <i className="fa-solid fa-rotate-left"></i>
                    {t('resetBtn', 'Reset')}
                  </button>
                </div>
              </Box>
            )}

            {/* Draft Selector & New Form Button */}
            <Box className="filter-boxes px-2 py-1 rounded-3 bg-white shadow-sm border">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <span className="small text-black fw-bold">
                  <i className="fa-solid fa-folder-open text-primary me-1"></i>
                  {t('drafts', 'Saved Forms')}
                </span>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div style={{ minWidth: '220px' }}>
                  <Select
                    value={selectedDraft}
                    onChange={handleDraftChange}
                    options={draftList}
                    placeholder={t('select_draft', 'Select draft...')}
                    isClearable
                    components={{
                      Option: CustomOption,
                      SingleValue: CustomSingleValue,
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menuList: (base) => ({
                        ...base,
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                      }),
                    }}
                  />
                </div>
                <div className="filter-box">
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={handleOpenNewFormModal}
                    startIcon={<i className="fa-solid fa-plus"></i>}
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    {t('new_form', 'New')}
                  </Button>
                </div>
              </div>
            </Box>

            {/* Action Bar */}
            {!isLoading && (
              <Box className="filter-boxes px-2 py-1 rounded-3 bg-white shadow-sm border">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="small text-black fw-bold">
                    <i className="fa-solid fa-file-circle-check text-primary me-1"></i>
                    {t('formActionsHeader', 'Form Actions')}
                  </span>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {!isPublished ? (
                    <>
                      <div className="filter-box">
                        <button
                          type="button"
                          onClick={handleInitiateSave}
                          className="generate-btn-modern"
                          disabled={!ready}
                        >
                          {t('save', 'Save')}
                        </button>
                      </div>

                      <div className="filter-btn ms-auto">
                        <button
                          type="button"
                          onClick={handleInitiatePublish}
                          className="button-tertiary"
                          disabled={!ready}
                        >
                          <img src={publishBudgetIcon} alt="" />
                          {t('save_publish', 'Publish')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <Button
                      startIcon={<i className="fa-solid fa-file-pdf"></i>}
                      variant="outlined"
                      color="success"
                      size="medium"
                      onClick={downloadPdf}
                      disabled={!ready}
                      title={t('download_pdf', 'Download PDF')}
                      sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    >
                      {t('download_pdf', 'Download PDF')}
                    </Button>
                  )}
                </div>
              </Box>
            )}
          </div>
        </div>
      </div>

      <div className="filter-btn">
        <div className={`page-loader ${loading || isLoading ? 'visible' : ''}`}>
          <div className="loader"></div>
        </div>
      </div>

      <div
        className="executive-dashboard p-0"
        style={{ maxHeight: 'calc(100vh - 170px)', overflowY: 'auto' }}
        ref={tableContainerRef}
      >
        <div className="position-absolute top-10 end-1 m-1 z-3 d-flex align-items-center gap-2">
          {revision && (
            <>
              <span className="text-primary px-2 text-black">
                Rev: <strong>{revision}</strong>
                {isLatestLoaded && ` (${t('latest', 'Latest')})`}
              </span>
              {newerVersionAvailable && !isPublished && (
                <Button
                  variant="text"
                  size="small"
                  onClick={loadLatestVersion}
                  disabled={isLoading}
                  sx={{
                    textTransform: 'none',
                    color: '#0d6efd',
                    fontWeight: 'bold',
                  }}
                >
                  {t('new_revision_available', 'New Revision Available')}
                </Button>
              )}
            </>
          )}
        </div>

        <div
          style={{
            padding: 0,
            margin: 0,
            textAlign: 'center',
            position: 'relative',
          }}
        >
          {hasError && !loading && !isLoading && (
            <div className="p-4 text-center">
              <p className="text-danger fw-bold">
                {t('failed_to_load_pdf', 'Failed to load PDF.')}
              </p>
              <button
                type="button"
                className="btn btn-primary btn-sm mt-2"
                onClick={() => loadForm(selectedDraft)}
              >
                {t('retry', 'Retry')}
              </button>
            </div>
          )}

          {ready && !hasError && (
            <Document
              file={documentFile}
              loading={
                <PdfLoadingView text={t('please_wait', 'Please wait...')} />
              }
              error={
                <PdfErrorView
                  title={t('failed_to_load_pdf', 'Failed to load PDF.')}
                  subtitle={t(
                    'pdf_render_error_sub',
                    'The document could not be rendered. Click retry to attempt reloading.'
                  )}
                  retryBtnText={t('retry')}
                  onRetry={() => loadForm(selectedDraft)}
                />
              }
            >
              {Array.from(new Array(numPages), (_, pageIndex) => {
                const viewport = getViewport(pageIndex)
                const meta = pageMeta[pageIndex]
                if (!viewport) return null
                return (
                  <div
                    key={pageIndex}
                    style={{
                      position: 'relative',
                      display: 'inline-block',
                      width: viewport.width,
                      height: viewport.height,
                      margin: '0 auto 16px auto',
                    }}
                  >
                    <Page
                      pageNumber={pageIndex + 1}
                      scale={meta.baseScale * zoom}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />

                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 10,
                      }}
                    >
                      {fieldDefs
                        .filter((def) => def.pageIndex === pageIndex)
                        .map((def) => renderOverlay(def))}
                    </div>
                  </div>
                )
              })}
            </Document>
          )}
        </div>
        <ScrollToggle scrollRef={tableContainerRef} />
      </div>

      {/* Modal: Select Language for New Form */}
      <Dialog
        open={newFormLangModalOpen}
        onClose={() => setNewFormLangModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box className="d-flex align-items-center" sx={{ mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'primary.50',
                mr: 1.25,
              }}
            >
              <i
                className="fa-solid fa-language"
                style={{ fontSize: 16, color: '#1d4ed8' }}
              />
            </Box>
            <Typography variant="h6" fontWeight={700}>
              {t('new_form_title', 'Create New Form 433-B')}
            </Typography>
          </Box>
          <Divider sx={{ borderColor: 'divider', opacity: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
            {t(
              'select_form_lang_desc',
              'Select the language template for this new form:'
            )}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, pb: 1 }}>
          <Box sx={{ mb: 1 }}>
            <Select
              value={LANGUAGE_OPTIONS.find(
                (opt) => opt.value === selectedNewLang
              )}
              onChange={(opt: any) => setSelectedNewLang(opt?.value || 'en-US')}
              options={LANGUAGE_OPTIONS}
              isSearchable={false}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setNewFormLangModalOpen(false)}
            color="inherit"
            sx={{ textTransform: 'none' }}
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleConfirmNewForm}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none', borderRadius: 1.5, px: 2.5 }}
          >
            {t('create_form', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal: Save Mode & Form Name */}
      <Dialog
        open={draftNameModalOpen}
        onClose={() => setDraftNameModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box className="d-flex align-items-center" sx={{ mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor:
                  pendingDraftAction === 'publish'
                    ? 'success.50'
                    : 'primary.50',
                mr: 1.25,
              }}
            >
              <i
                className={
                  pendingDraftAction === 'publish'
                    ? 'fa-solid fa-paper-plane'
                    : 'fa-solid fa-floppy-disk'
                }
                style={{
                  fontSize: 14,
                  color:
                    pendingDraftAction === 'publish' ? '#166534' : '#1d4ed8',
                }}
              />
            </Box>
            <Typography variant="h6" fontWeight={700}>
              {pendingDraftAction === 'publish'
                ? t('name_publish_draft', 'Publish Form')
                : t('save_draft_options', 'Save Form')}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: 'divider', opacity: 1 }} />
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {selectedDraft && (
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <RadioGroup
                value={saveMode}
                onChange={(e) =>
                  setSaveMode(e.target.value as 'update' | 'new')
                }
                sx={{ gap: 1 }}
              >
                {[
                  {
                    value: 'update',
                    title: t('update_existing_draft', 'Update Current Draft'),
                    detail: `${selectedDraft.label} (${
                      selectedDraft.lang === 'es' ||
                      selectedDraft.lang?.startsWith('es-')
                        ? 'Spanish'
                        : 'English'
                    })`,
                  },
                  {
                    value: 'new',
                    title: t('save_as_new_draft', 'Save as a New Draft'),
                    detail: t('detail_info', {
                      language:
                        activeFormLang === 'es'
                          ? "Spanish"
                          : "English",
                    }),
                  },
                ].map((option) => (
                  <Box
                    key={option.value}
                    onClick={() =>
                      setSaveMode(option.value as 'update' | 'new')
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      p: 1.25,
                      border: '1px solid',
                      borderColor:
                        saveMode === option.value ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      bgcolor:
                        saveMode === option.value
                          ? 'primary.50'
                          : 'transparent',
                      cursor: 'pointer',
                      transition:
                        'border-color 0.15s ease, background-color 0.15s ease',
                    }}
                  >
                    <Radio
                      size="small"
                      value={option.value}
                      checked={saveMode === option.value}
                      sx={{ p: 0, mt: '2px' }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {option.title}
                      </Typography>
                      {option.detail && (
                        <Typography variant="caption" color="text.secondary">
                          {option.detail}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.75, display: 'block' }}
          >
            {saveMode === 'new' || !selectedDraft
              ? t('enter_new_draft_name', 'Enter a name for the new draft')
              : t('enter_updated_draft_name', 'Form Name')}
          </Typography>

          <TextField
            autoFocus
            fullWidth
            size="small"
            value={draftNameInput}
            inputProps={{ maxLength: 30 }}
            onChange={(e) => setDraftNameInput(e.target.value.slice(0, 30))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleModalSubmit()
            }}
            InputProps={{ sx: { borderRadius: 1.5 } }}
            helperText={`${draftNameInput.length}/30`}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setDraftNameModalOpen(false)}
            color="inherit"
            sx={{ textTransform: 'none' }}
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleModalSubmit}
            variant="contained"
            color={pendingDraftAction === 'publish' ? 'success' : 'primary'}
            disabled={!draftNameInput.trim()}
            sx={{ textTransform: 'none', borderRadius: 1.5, px: 2.5 }}
          >
            {pendingDraftAction === 'publish'
              ? t('continue_publish', 'Continue')
              : saveMode === 'new' || !selectedDraft
              ? t('save_new', 'Save as New')
              : t('update', 'Update')}
          </Button>
        </DialogActions>
      </Dialog>

      <SAlert
        msg={t('publish_confirmation', { formTitle: 'Form 433-B' })}
        show={publishAlert}
        title={t('are_you_sure', 'Are you sure?')}
        onConfirm={() => {
          setPublishAlert(false)
          executePublishDraft(
            draftNameInput,
            saveMode === 'new' || !selectedDraft
          )
        }}
        confirmBtnText={t('yes_publish', 'Yes, Publish')}
        cancelBtnText={t('cancel', 'Cancel')}
        showCancel={true}
        onCancel={() => setPublishAlert(false)}
      />

      <NavigationBlockerDialog
        open={showPrompt}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
    </div>
  )
}
